/**
 * Serialize a React Flow canvas (the design-mode builder) into a
 * `TopologyDefinition` the API accepts, plus a light client-side validator that
 * mirrors the essential rules of core's `validateTopology` so we can surface
 * errors before POSTing.
 */

import type { Node, Edge } from "@xyflow/react";
import { defaultSecurity, type AgentSecurityProfile } from "../data/security-defaults.js";

export interface BuilderAgentData {
  name?: string;
  label?: string;
  backend?: string;
  model?: string;
  description?: string;
  identity?: {
    persona?: string;
    instructions?: string | string[];
    outputFormat?: string;
    constraints?: string[];
  };
  capabilities?: string[];
  security?: AgentSecurityProfile;
  isEntryPoint?: boolean;
  isExitPoint?: boolean;
  [key: string]: unknown;
}

export interface TopologyDefinitionDraft {
  type: string;
  agents: Array<{
    id: string;
    name: string;
    description: string;
    backend: string;
    model?: string;
    identity: { persona: string; instructions: string | string[]; outputFormat?: string; constraints?: string[] };
    capabilities: string[];
    security: AgentSecurityProfile;
  }>;
  edges: Array<{ from: string; to: string; type: string; bidirectional: boolean }>;
  entryPoint: string;
  exitPoints: string[];
}

/** Build a topology definition from the canvas nodes and edges. */
export function flowToTopology(nodes: Node[], edges: Edge[], type: string): TopologyDefinitionDraft {
  const agents = nodes.map((n) => {
    const d = (n.data ?? {}) as BuilderAgentData;
    return {
      id: n.id,
      name: d.name || d.label || n.id,
      description: d.description || `${d.name || d.label || n.id} agent`,
      backend: d.backend || "claude-agent-sdk",
      ...(d.model ? { model: d.model } : {}),
      identity: {
        persona: d.identity?.persona || "You are a helpful agent.",
        instructions: d.identity?.instructions || "Complete the task.",
        ...(d.identity?.outputFormat ? { outputFormat: d.identity.outputFormat } : {}),
        ...(d.identity?.constraints?.length ? { constraints: d.identity.constraints } : {}),
      },
      capabilities: d.capabilities ?? [],
      security: d.security ?? defaultSecurity("semi-trusted"),
    };
  });

  const flowEdges = edges.map((e) => {
    const data = (e.data ?? {}) as { edgeType?: string; bidirectional?: boolean };
    return {
      from: e.source,
      to: e.target,
      type: data.edgeType || "delegates-to",
      bidirectional: data.bidirectional ?? false,
    };
  });

  const entryNode = nodes.find((n) => (n.data as BuilderAgentData)?.isEntryPoint);
  const exitPoints = nodes
    .filter((n) => (n.data as BuilderAgentData)?.isExitPoint)
    .map((n) => n.id);

  return {
    type,
    agents,
    edges: flowEdges,
    entryPoint: entryNode?.id ?? "",
    exitPoints,
  };
}

/** Essential, client-side validation mirroring core's `validateTopology`. */
export function validateDefinition(def: TopologyDefinitionDraft): string[] {
  const errors: string[] = [];
  const ids = new Set(def.agents.map((a) => a.id));

  if (def.agents.length === 0) {
    errors.push("A topology needs at least one agent.");
  }

  if (!def.entryPoint) {
    errors.push("Pick an entry point — mark one agent as the entry.");
  } else if (!ids.has(def.entryPoint)) {
    errors.push(`Entry point "${def.entryPoint}" is not one of the agents.`);
  } else {
    const entry = def.agents.find((a) => a.id === def.entryPoint);
    if (entry?.security?.mode === "read-only") {
      errors.push(`Entry agent "${entry.name}" is read-only — entry agents must be read-write.`);
    }
  }

  if (def.exitPoints.length === 0) {
    errors.push("Pick at least one exit point.");
  }
  for (const exit of def.exitPoints) {
    if (!ids.has(exit)) errors.push(`Exit point "${exit}" is not one of the agents.`);
  }

  for (const e of def.edges) {
    if (!ids.has(e.from)) errors.push(`A connection starts from unknown agent "${e.from}".`);
    if (!ids.has(e.to)) errors.push(`A connection points to unknown agent "${e.to}".`);
  }

  return errors;
}
